import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

import { untilDestroyed, Logger } from '../../../core';
import { AuthService } from '../../services/auth.service';
import { ChangePasswordBody } from '../../../core/models/change-password-request-body';
import { CustomValidators } from '../../../core/classes/custom-validators/custom-validators';
import { SHARED_IMPORTS } from '@app/shared/shared.imports';

const log = new Logger('ChangePassword');

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: true,
  imports: [...SHARED_IMPORTS]
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  error: string | undefined;
  changepasswordForm!: FormGroup;
  isLoading = false;
  showSuccessMgs: boolean;
  token: string;
  email: string;
  hidePassword = true;
  hideConfirmPassword = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private authService: AuthService
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.token = this.route.snapshot.params.token || null;
    this.email = this.route.snapshot.params.email || null;
  }

  changePassword() {
    this.isLoading = true;
    const body: ChangePasswordBody = {
      email: this.email,
      token: this.token,
      password: this.changepasswordForm.get('password').value,
      password_confirmation: this.changepasswordForm.get('confirmPassword').value
    };
    const change$ = this.authService.changePassword(body);
    change$
      .pipe(
        finalize(() => {
          this.changepasswordForm.markAsPristine();
          this.isLoading = false;
        }),
        untilDestroyed(this)
      )
      .subscribe(
        (data: boolean) => {
          log.debug(`${data} successfully logged in`);
          if (data === true) {
            this.showSuccessMgs = data;
          }
          this.router.navigate(['/login'], { replaceUrl: true });
        },
        (err: any) => {
          const error = err;
          log.debug(`reseting password error: ${error}`);
          if (error) {
            if (error.error.message) {
              this.error = error.error.message;
            }
          } else {
            this.error = 'Reseting password error.';
          }
        }
      );
  }

  ngOnDestroy() {}

  private createForm() {
    this.changepasswordForm = this.formBuilder.group(
      {
        password: [
          '',
          Validators.compose([
            // 1. Password Field is Required
            Validators.required,
            // 2. check whether the entered password has a number
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            // 3. check whether the entered password has upper case letter
            CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
            // 4. check whether the entered password has a lower-case letter
            CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
            // 5. check whether the entered password has a special character
            CustomValidators.patternValidator(/[&]|[-]|[_]|[.]|[!]|[@]|[#]|[$]|[%]|[*]|[?]|[+]|[~]/, {
              hasSpecialCharacters: true
            }),
            // 6. Has a minimum length of 8 characters
            Validators.minLength(8)
          ])
        ],

        confirmPassword: ['', [Validators.compose([Validators.required])]]
      },
      {
        validator: CustomValidators.passwordMatchValidator
      }
    );
  }
}
